/*
 * Lazy load background images using IntersectionObserver
 * https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 * Usage: Add the class "lazyload-background" to the element and set the data-bgimage attribute to the image URL
 * <div class="lazyload-background" data-bgimage="image.jpg"></div>
 *
 * For dynamically loaded content, call window.madLazyBackgroundImage.observe(container) after adding new elements
*/

(function() {
  'use strict';

  const isLocalEnvironment = location.hostname.includes('.local');
  let globalObserver = null;

  /**
   * Initialize or get the global IntersectionObserver instance
   */
  function getObserver() {
    if (!globalObserver && 'IntersectionObserver' in window && !isLocalEnvironment) {
      globalObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              const bgImage = entry.target.dataset.bgimage;

              if (bgImage) {
                entry.target.style.backgroundImage = `url('${bgImage}')`;
              }

              globalObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '150px' } // Load images before they come into the viewport
      );
    }
    return globalObserver;
  }

  /**
   * Apply lazy loading to elements within a container
   * @param {Element|null} container - The container element to search within, or null for document
   */
  function observeLazyBackgrounds(container) {
    if (isLocalEnvironment) return; // Disable lazy loading on local environment

    const root = container || document;
    const lazyBackgroundElements = root.querySelectorAll('.lazyload-background');

    if ('IntersectionObserver' in window) {
      const observer = getObserver();

      // Observe all elements with the .lazyload-background class
      lazyBackgroundElements.forEach(function (element) {
        if (element.dataset.bgimage && !element.style.backgroundImage) {
          observer.observe(element);
        }
      });

      // Use requestIdleCallback to load images when the browser is idle
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          function () {
            lazyBackgroundElements.forEach(function (element) {
              const bgImage = element.dataset.bgimage;

              if (bgImage && !element.style.backgroundImage) {
                element.style.backgroundImage = `url('${bgImage}')`;
                observer.unobserve(element);
              }
            });
          },
          { timeout: 2000 } // Fallback if the browser is busy for more than 2 seconds
        );
      }
    } else {
      // Fallback for browsers without IntersectionObserver support
      lazyBackgroundElements.forEach(function (element) {
        const bgImage = element.dataset.bgimage;
        if (bgImage && !element.style.backgroundImage) {
          element.style.backgroundImage = `url('${bgImage}')`;
        }
      });
    }
  }

  // Expose public API
  window.madLazyBackgroundImage = {
    observe: observeLazyBackgrounds
  };

  // Initialize on page load
  jQuery(document).ready(function () {
    observeLazyBackgrounds(null);
  });
})();